const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

// Typing finishes slightly before the panel is fully expanded so the last
// character never depends on sub-pixel scroll rounding.
const typingCompletionProgress = 0.9

const initScrollTyping = (track) => {
  const sentences = Array.from(track.querySelectorAll("[data-scroll-type-sentence]"), (element) => ({
    characters: Array.from(element.textContent.trim()),
    element
  }))
  const totalCharacters = sentences.reduce((total, sentence) => total + sentence.characters.length, 0)

  if (totalCharacters === 0) return () => {}

  let previousCharacterCount = -1
  track.dataset.scrollTypeReady = "true"

  return (progress) => {
    const typingProgress = clamp(progress / typingCompletionProgress, 0, 1)
    const characterCount = Math.round(totalCharacters * typingProgress)
    if (characterCount === previousCharacterCount) return

    let remainingCharacters = characterCount

    sentences.forEach((sentence) => {
      const visibleCharacters = Math.min(remainingCharacters, sentence.characters.length)
      sentence.element.textContent = sentence.characters.slice(0, visibleCharacters).join("")
      remainingCharacters = Math.max(remainingCharacters - sentence.characters.length, 0)
    })

    previousCharacterCount = characterCount
  }
}

export function initScrollExpandSections() {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return

  document.querySelectorAll("[data-scroll-expand]").forEach((track) => {
    if (track.dataset.scrollExpandReady === "true") return

    const panel = track.querySelector("[data-scroll-expand-panel]")
    if (!panel) return

    track.dataset.scrollExpandReady = "true"

    const startRatio = Number.parseFloat(track.dataset.scrollExpandStartRatio) || 0.55
    const minimumHeight = Number.parseFloat(track.dataset.scrollExpandMinHeight) || 384
    const updateTyping = initScrollTyping(track)
    let metrics
    let frame

    const measure = () => {
      const header = document.querySelector(".storefront-header")
      const headerHeight = header?.offsetHeight || 0
      const viewportHeight = Math.max(window.innerHeight - headerHeight, 1)

      track.style.height = ""
      panel.style.height = ""
      const fullHeight = Math.max(viewportHeight, panel.offsetHeight)
      const initialHeight = Math.min(fullHeight, Math.max(window.innerHeight * startRatio, minimumHeight))

      metrics = { fullHeight, headerHeight, initialHeight }
      track.style.height = `${fullHeight}px`
      update()
    }

    const update = () => {
      if (!metrics) return

      const { fullHeight, headerHeight, initialHeight } = metrics
      const expansionDistance = fullHeight - initialHeight
      const trackTop = track.getBoundingClientRect().top
      const startTop = window.innerHeight - initialHeight
      const scrollDistance = Math.max(startTop - headerHeight, 1)
      const progress = expansionDistance > 0
        ? clamp((startTop - trackTop) / scrollDistance, 0, 1)
        : 1
      const height = initialHeight + expansionDistance * progress

      panel.style.height = `${height}px`
      track.style.setProperty("--scroll-expand-progress", progress.toFixed(4))
      track.style.setProperty("--scroll-expand-header-height", `${headerHeight}px`)
      updateTyping(progress)
      frame = undefined
    }

    const scheduleUpdate = () => {
      if (!frame) frame = window.requestAnimationFrame(update)
    }

    window.addEventListener("scroll", scheduleUpdate, { capture: true, passive: true })
    window.addEventListener("resize", measure, { passive: true })
    measure()
  })
}
